'use strict';
/* ==========================================================================
   levels/tags.js — step "tags" (orientation any, notebook style): PRICE TAGS (DESIGN.md §5.2).

   Six price boards per attempt from the pool `tags` (content/bank.tags.json through Ratio.pool → core/bank.js:
   draw.n = 6, draw.unique = "cheat" so no cheat twice, draw.require = ["legit"] so exactly one honest board), served
   fewest rows first. Every board is built by the model: Ratio.board(item, params) → honest rows y = p·x with the one
   cheat applied at row `bad` (core-local/ratio.js); every number on screen (the rows, the working, the honest value,
   the scales) comes from it. The "What's p?" options are the bank instance's (formula + named distractors).

   A board drops onto the page:  kind "table" = an index card with ruled rows (x | y, the item's labels, money as $),
                                 kind "graph" = ink axes with labelled scales, the rows as dots (the offLine point sits
                                 one grid step above the line; the honest line is drawn faintly only AFTER the answer).
   Per board:  drop → spot: tap the row / point that breaks the pattern, or "All proportional". A tap on an honest row is
                     a miss: "That row is fine: $18.00 ÷ 2 = 9" (a pencil tick and its working beside it); after 3
                     misses Ms. Rivera circles it herself. "÷" beside a row (under a point) shows its working for free.
             → p: "What's p?" (3 options); a wrong pick is crossed out with its `why`, pick again
             → fix: Rivera's red pen strikes the wrong value and writes the honest one beside it (graph: the honest
                    point + the faint honest line through (0, 0)); her card: y ÷ x for every row, the odd one circled,
                    the item's filled `explain` → next board.
   Stars: right row first try = 1 + right p first try = 1 (legit: "All proportional" first try = 1 + p = 1). of = 12,
   pass = pool.passStars (8). Game.progress.scores.tags = {best, last, of}; first answers under
   Game.progress.answers['tags/<key>/row'] and ['tags/<key>']; Bank.finish at the end; the stamp (Game.complete) through
   "Collect your stamp" on a pass (a pass that leaves without collecting keeps it). Resume: Game.progress.bank.tags.run
   = {i, ev, res} (the current board's taps / checks / picks are replayed after a reload).

   Portrait phone (Dock.active): the stage shows the board large (a picture); rows / points are Dock.choices, "All
   proportional" a dock button, the ÷ checks dock buttons, p the Dock.choices with the why as notes, the fix a list.
   Landscape phone: the rows / points / ÷ are canvas tap targets ≥ 44 CSS px (UI.tap).
   Keys: 1–9 = row / point n (numbers pencilled beside them), A = All proportional, 1–3 = p option, Enter = next.

   QA hooks: sc.phase ('intro' | 'drop' | 'spot' | 'p' | 'fix' | 'end'), sc.board (the Ratio.board result),
   sc.answer() (the bad row index or 'legit'), sc.tapRow(i), sc.legit(), sc.choose(i), sc.check(i), sc.next(),
   sc.begin(fresh), sc.collect(), sc.back(); sc.inst, sc.instances, sc.bi, sc.results, sc.stars, sc.seed, sc.attempt,
   sc.resumed, sc.misses, sc.picks, sc.marks, sc.checked; Levels.tags.solution ({legit, row, answer, p}) and
   Levels.tags.rows() (the tap rects of the live board on the canvas).
   ========================================================================== */
(function () {
  const STEP = 'tags';
  const MAX_MISSES = 3, DROP = 0.7, NEXT_GUARD = 0.3;
  const pool = () => {
    const p = Bank.pool(STEP);
    return p && p.items.length ? p : Bank.load(Ratio.pool(Game.content.bank_tags || { pool: STEP, draw: { n: 0 }, items: [] }));
  };
  const SHORT = { additive: 'added, not multiplied', fee: 'a fee stuck on', bulk: 'a bulk deal', slip: 'one too many', offLine: 'a point off the line', legit: 'all proportional' };
  const INK = () => NB.PAL.ink, RED = () => NB.PAL.redpen, PENCIL = () => NB.PAL.pencil;
  const uiF = (s, w = 400) => `${w} ${s}px ${NB.FONT.ui}`, handF = (s, w = 400) => `${w} ${s}px ${NB.FONT.hand}`;
  const tw = (s, f) => { g.save(); g.font = f; const w = g.measureText(s).width; g.restore(); return w; };
  const prog = (t0, d) => Math.max(0, Math.min(1, (T - t0) / d));

  /* one board: the model's board + the bank instance */
  function round(inst) {
    const b = Ratio.board(inst.item, inst.params);
    return { inst, b, legit: b.cheat === 'legit', bad: b.bad, graph: b.kind === 'graph', n: b.rows.length };
  }
  const xText = r => Ratio.text(r.xq);
  const yNum = r => Ratio.text(r.yq);
  /* a row as words (the dock's buttons, the recorded first answer) */
  const rowLabel = (R, i) => { const r = R.b.rows[i]; return R.graph ? `(${xText(r)}, ${yNum(r)})` : `${xText(r)} → ${r.text}`; };
  const unitOf = R => (R.graph ? 'point' : 'row');

  /* ---------------- layouts (mode: 'm' mouse / desktop, 't' touch landscape, 'd' the portrait stage picture) ---------------- */
  function tableLay(b, mode) {
    const n = b.rows.length;
    let x, w, top, head, colH, rowH, size;
    if (mode === 'd') {
      x = 150; w = 1260; top = 24; head = 150; colH = 92;
      rowH = Math.min(170, Math.floor((1060 - top - head - colH - 24) / n)); size = Math.round(Math.min(84, rowH * 0.5));
    } else {
      x = 210; w = 540; head = 96; colH = 58;
      rowH = mode === 't' ? Math.max(90, Math.ceil(UI.tap(64)) + 6) : 90; size = Math.round(UI.text(46));
      top = 215;
    }
    const h = head + colH + n * rowH + 22;
    if (mode !== 'd' && top + h > 1066) top = Math.max(96, 1066 - h);
    const r = mode === 'd' ? 0 : UI.tap(52) / 2;
    const rows = b.rows.map((row, i) => { const rt = top + head + colH + i * rowH; return { i, top: rt, h: rowH, cy: rt + rowH / 2, cx: x + w / 2, w }; });
    const chkX = x + w + 16 + r, noteX = mode === 'd' ? x + w + 30 : chkX + r + 16;
    return { kind: 'table', mode, x, y: top, w, h, head, colH, rowH, size, split: x + Math.round(w * 0.38), rows, r, chkX, noteX,
      panelX: mode === 'd' ? 1900 : Math.max(1150, noteX + 300) };
  }
  function graphLay(b, mode) {
    const G = b.grid, docked = mode === 'd';
    const touch = mode === 't', x = docked ? 170 : touch ? 190 : 200, y = docked ? 20 : 205, w = docked ? 1420 : touch ? 950 : 880, h = docked ? 1040 : 850;
    const head = docked ? 120 : 84, r = docked ? 0 : UI.tap(52) / 2;
    const fs = docked ? 40 : Math.round(UI.text(24)), an = Math.round(fs * 1.05);
    const bottom = fs * 1.05 + an * 1.25 + an * 0.7 + (docked ? 10 : 2 * r + 16);
    const ox = x + (docked ? 190 : 130), oy = y + h - bottom, plotTop = y + head + an * 1.9 + 8;
    const plotW = x + w - (docked ? 60 : 50) - ox, plotH = oy - plotTop;
    const sx = plotW / G.xSquares, sy = plotH / G.ySquares;
    const X = v => ox + v * sx, Y = v => oy - v / G.yStep * sy;
    const hit = Math.max(64, UI.tap(64));
    const rows = b.rows.map((row, i) => ({ i, px: X(row.x), py: Y(row.y), hx: X(row.x), hy: Y(row.y), hw: Math.min(hit, sx), hh: hit }));
    return { kind: 'graph', mode, x, y, w, h, head, r, ox, oy, plotTop, plotW, plotH, sx, sy, X, Y, rows, G,
      dot: docked ? 17 : UI.touch() ? 13 : 11, fs, an, nameY: oy + fs * 1.05 + an * 1.25, chipY: oy + fs * 1.05 + an * 1.25 + an * 0.7 + r + 6, panelX: docked ? 1900 : x + w + 40 };
  }

  class Tags extends Scene {
    get pool() { return pool(); }
    get n() { return this.instances ? this.instances.length : this.pool.draw.n; }
    get of() { return this.n * 2; }
    get passStars() { const j = this.pool.json || {}; return Math.min(this.of, j.passStars || Math.ceil(this.of * 2 / 3)); }
    get inst() { return this.R ? this.R.inst : null; }
    get board() { return this.R ? this.R.b : null; }
    get state() { return this.phase; }                                          // alias (the receipts tests' name)
    get solution() { const R = this.R; return R ? { legit: R.legit, row: R.bad, answer: R.legit ? 'legit' : R.bad, p: R.inst.answerIndex, pText: R.inst.options[R.inst.answerIndex] } : null; }
    answer() { return this.R ? (this.R.legit ? 'legit' : this.R.bad) : null; }
    get stars() { return (this.results || []).reduce((a, r) => a + (r ? r.stars : 0), 0); }

    enter() {
      this.phase = 'intro'; this.instances = null; this.R = null; this.bi = 0; this.results = []; this.passed = false; this.collected = false;
      this.lays = new Map();
      Audio.play('paper', { gain: 0.5 });
      const st = Game.progress.bank && Game.progress.bank[STEP];
      if (st && st.open && st.run && st.pool === STEP) this.begin();              // an attempt in progress: straight back in
    }
    exit() { this.bankStamp(); }
    back() { Game.replace(new Levels.map()); }

    /* ---- the attempt ---- */
    begin(fresh = false) {
      this.bankStamp();
      const S = Game.progress.bank || {}, prev = S[STEP] && S[STEP].open ? S[STEP].run : null;
      const a = Bank.attempt(STEP, pool(), { fresh });
      // fewer rows first (DESIGN.md §5.2); stable, so ties keep the draw order
      this.instances = a.instances.map((inst, k) => ({ inst, k, n: inst.item.xs.length })).sort((p, q) => p.n - q.n || p.k - q.k).map(o => o.inst);
      this.seed = a.seed; this.attempt = a.attempt; this.resumed = a.resumed;
      this.results = []; this.passed = false; this.collected = false;
      if (a.resumed && prev && this.restore(prev)) return;
      this.startBoard(0);
    }
    restore(run) {
      try {
        if (!(run.i >= 0 && run.i < this.instances.length)) return false;
        this.results = (run.res || []).slice(0, run.i);
        this.setup(run.i);
        this.boardAt = Game.time - DROP - 1; this.phase = 'spot'; this.landed = true;
        this.quiet = true;
        for (const [k, v] of run.ev || []) { if (k === 't') this.tapRow(v); else if (k === 'l') this.legit(); else if (k === 'c') this.choose(v); else if (k === 'k') this.check(v); }
        this.quiet = false;
        this.save();
        return true;
      } catch (e) { this.quiet = false; console.warn('[tags] could not resume: ' + e.message); return false; }
    }
    setup(i) {
      this.bi = i; this.R = round(this.instances[i]); this.lays.clear();
      this.ev = []; this.misses = 0; this.marks = {}; this.checked = {}; this.lastCheck = -1; this.found = -1; this.revealed = false;
      this.picks = []; this.say = null; this.rowStar = 0; this.landed = false; this._fc = null;
      this.boardAt = Game.time; this.foundAt = this.pAt = this.fixAt = this.pickedAt = null;
    }
    startBoard(i) {
      this.setup(i); this.phase = 'drop';
      this.save();
      this.sfx('whoosh', { gain: 0.35 });
    }
    save() {
      const S = Game.progress.bank && Game.progress.bank[STEP];
      if (S && S.open && this.phase !== 'end') S.run = { i: this.bi, ev: this.ev.slice(), res: this.results.slice() };
      Game.progress.save();
    }
    sfx(name, o) { if (!this.quiet && (!Audio.has || Audio.has(name))) Audio.play(name, o); }
    pen() { this.sfx(Audio.has('pen') ? 'pen' : 'scribble', { gain: 0.45 }); }

    /* ---- ÷: a row's working, free (never an answer, never costs a star) ---- */
    check(i) {
      const R = this.R;
      if (!R || !(i >= 0 && i < R.n) || this.phase === 'intro' || this.phase === 'end') return false;
      if (this.phase === 'drop') this.phase = 'spot';
      this.ev.push(['k', i]);
      this.checked[i] = this.checked[i] || { at: Game.time };
      this.lastCheck = i; this.lastCheckAt = Game.time;
      this.sfx(Audio.has('beep') ? 'beep' : 'pop', { gain: 0.3 });
      this.save();
      return true;
    }
    working(i) { return this.R ? Ratio.rowWorking(this.R.b, i) : ''; }

    /* ---- spot the row that breaks the pattern ---- */
    tapRow(i) {
      const R = this.R;
      if (this.phase === 'drop') this.phase = 'spot';                               // a tap while it drops: it has landed
      if (this.phase !== 'spot' || !R || !(i >= 0 && i < R.n) || this.marks[i]) return false;
      this.ev.push(['t', i]);
      Game.progress.answer(STEP + '/' + R.inst.key + '/row', rowLabel(R, i));      // first try only
      if (!R.legit && i === R.bad) { this.rowStar = this.misses === 0 ? 1 : 0; this.foundRow(i, false); this.save(); return true; }
      this.marks[i] = { at: Game.time };
      this.checked[i] = this.checked[i] || { at: Game.time };
      this.miss(`That ${unitOf(R)} is fine: ${this.working(i)}.`);
      this.save();
      return true;
    }
    legit() {
      const R = this.R;
      if (this.phase === 'drop') this.phase = 'spot';
      if (this.phase !== 'spot' || !R) return false;
      this.ev.push(['l']);
      Game.progress.answer(STEP + '/' + R.inst.key + '/row', 'All proportional');   // first try only
      if (R.legit) { this.rowStar = this.misses === 0 ? 1 : 0; this.toP(); this.sfx('ding', { gain: 0.6 }); this.save(); return true; }
      this.miss(`Not all proportional: one ${unitOf(R)} breaks the pattern. Divide y by x in every ${unitOf(R)}.`);
      this.save();
      return true;
    }
    miss(text) {
      this.misses++; this.say = { text, at: Game.time };
      this.sfx('thunk', { gain: 0.4, rate: 0.9 });
      if (this.misses >= MAX_MISSES) {
        const R = this.R;
        this.rowStar = 0;
        if (R.legit) { this.say = { text: `Every ${unitOf(R)} checks out: it's all proportional.`, at: Game.time }; this.revealed = true; this.toP(); }
        else { this.say = { text: `Ms. Rivera circles it: ${this.working(R.bad)}.`, at: Game.time }; this.foundRow(R.bad, true); }
      }
    }
    foundRow(i, revealed) {
      this.found = i; this.revealed = revealed; this.foundAt = Game.time;
      this.checked[i] = this.checked[i] || { at: Game.time };
      if (!revealed) this.say = null;
      this.pen();
      this.toP(0.6);
    }
    toP(delay = 0.4) { this.phase = 'p'; this.pAt = Game.time + delay; }

    /* ---- What's p? ---- */
    choose(i) {
      const R = this.R, q = this.inst;
      if (this.phase !== 'p' || !q || !(i >= 0 && i < q.options.length) || this.picks.includes(i)) return false;
      this.ev.push(['c', i]);
      if (!this.picks.length) Game.progress.answer(STEP + '/' + q.key, q.options[i]);   // first try only
      this.picks.push(i); this.pickedAt = Game.time;
      if (Bank.check(q, i)) {
        const pStar = this.picks.length === 1 ? 1 : 0;
        this.sfx('ding', { gain: 0.7 });
        this.toFix(this.rowStar, pStar);
      } else this.sfx('thunk', { gain: 0.35, rate: 0.8 });
      this.save();
      return true;
    }
    toFix(rowStar, pStar) {
      const R = this.R;
      this.phase = 'fix'; this.fixAt = Game.time; this._fc = null;
      this.results[this.bi] = { id: R.inst.id, key: R.inst.key, cheat: R.b.cheat, legit: R.legit, title: R.b.title, kind: R.b.kind, misses: this.misses,
        revealed: this.revealed, wrongPicks: this.picks.length - 1, rowStar, pStar, stars: rowStar + pStar };
      this.pen();
    }
    next() {
      if (this.phase !== 'fix' || Game.time - this.fixAt < NEXT_GUARD) return false;
      if (this.bi < this.instances.length - 1) this.startBoard(this.bi + 1);
      else this.finish();
      return true;
    }
    finish() {
      const s = this.stars;
      this.phase = 'end'; this.endAt = Game.time; this.passed = s >= this.passStars;
      const S = Game.progress.bank && Game.progress.bank[STEP]; if (S) delete S.run;
      Bank.finish(STEP);                                                             // the next attempt draws new boards
      const P_ = Game.progress, prev = P_.scores[STEP];
      P_.scores[STEP] = { best: Math.max(prev && prev.best || 0, s), last: s, of: this.of };
      P_.save();
      Audio.play(this.passed ? 'sparkle' : 'paper');
    }
    /* a pass that leaves without "Collect your stamp" still keeps its stamp */
    bankStamp() {
      if (this.passed && !this.collected) { this.collected = true; Game.progress.stamps[STEP] = true; Game.progress.save(); Game._justStamped = STEP; }
    }
    collect() { if (this.collected) return; this.collected = true; Game.complete(STEP); }
    /* tests only: end the attempt now with the stars so far and stamp the step */
    done() {
      if (!this.instances) this.begin();
      if (this.phase !== 'end') this.finish();
      this.collected = true; Game.complete(STEP);
    }
    endButtons() {
      return this.passed
        ? [{ label: 'Collect your stamp', go: () => this.collect() }, { label: 'Try again (new boards)', go: () => this.begin() }]
        : [{ label: 'Try again (new boards)', go: () => this.begin() }, { label: 'Back to the map', go: () => this.back() }];
    }

    update() {
      if (this.phase === 'drop' && Game.time - this.boardAt > DROP + 0.1) this.phase = 'spot';
      if (this.R && !this.landed && Game.time - this.boardAt > DROP * 0.7) { this.landed = true; this.sfx('paper', { gain: 0.45, rate: 1.1 }); }
    }
    keyDown(k) {
      const n = parseInt(k, 10);
      if (this.phase === 'intro') { if (k === 'Enter' || k === ' ') this.begin(); }
      else if (this.phase === 'drop' || this.phase === 'spot') { if (n >= 1 && n <= 9) this.tapRow(n - 1); else if (k === 'a' || k === 'A') this.legit(); }
      else if (this.phase === 'p') { if (n >= 1 && n <= 3) this.choose(n - 1); }
      else if (this.phase === 'fix') { if (k === 'Enter' || k === ' ') this.next(); }
      else if (this.phase === 'end') { if (k === 'Enter') this.endButtons()[0].go(); }
    }

    /* ---------------- drawing ---------------- */
    lay(docked) {
      const mode = docked ? 'd' : UI.touch() ? 't' : 'm', key = mode + '|' + Math.round(UI.tap(64)) + '|' + this.bi;
      let L = this.lays.get(key);
      if (!L) { L = this.R.graph ? graphLay(this.R.b, mode) : tableLay(this.R.b, mode); this.lays.set(key, L); }
      return L;
    }
    sub() {
      if (!this.instances || this.phase === 'intro' || this.phase === 'end') return '';
      return `board ${this.bi + 1} of ${this.n} · ${this.stars} star${this.stars === 1 ? '' : 's'}`;
    }
    draw() {
      const docked = UI.docked();
      NB.ground();
      UI.hud('Price Tags', this.sub());
      if (this.phase === 'intro') return this.drawIntro(docked);
      if (this.phase === 'end') return this.drawEnd(docked);
      const L = this.lay(docked), R = this.R;
      const s = E.outBack(Math.max(0, Math.min(1, (T - this.boardAt) / DROP)), 1.1);
      g.save(); g.translate(0, (1 - s) * -1000); g.rotate((1 - s) * -0.05);
      const st = this.boardState(docked);
      if (R.graph) this.drawGraph(R, L, st); else this.drawTable(R, L, st);
      g.restore();
      if (docked) return this.drawDock(L);
      if (this.phase === 'drop' || this.phase === 'spot') this.drawSpot(L);
      else if (this.phase === 'p') this.drawP(L);
      else this.drawFix(L);
    }
    boardState(docked) {
      const spot = this.phase === 'spot' || this.phase === 'drop';
      return { docked, spot, live: this.phase === 'spot' && !docked, marks: this.marks, checked: this.checked, found: this.found, foundAt: this.foundAt,
        fix: this.phase === 'fix', fixAt: this.fixAt, bi: this.bi, keys: UI.keys && spot && !docked, lastCheck: this.lastCheck };
    }

    /* ---- the index-card table ---- */
    drawTable(R, L, st) {
      const b = R.b, { x, y, w, h, head, colH, rowH, size, split } = L, docked = L.mode === 'd';
      NB.indexCard(x, y, w, h, { lines: false });
      g.save();
      g.fillStyle = RED(); g.fillRect(x, y + head - 1, w, 2.5);
      g.fillStyle = NB.PAL.cardLine;
      for (let i = 1; i <= b.rows.length; i++) g.fillRect(x, y + head + colH + i * rowH - 1, w, 1.5);
      g.fillStyle = INK(); g.fillRect(x + 14, y + head + colH - 2, w - 28, 3);
      g.fillRect(split - 1.5, y + head + 10, 3, colH + b.rows.length * rowH - 10);
      g.restore();
      NB.write(b.title, x + 30, y + head * 0.52, { size: Math.round(head * 0.5), weight: 700 });
      if (b.money && Levels.title && Levels.title.priceTag) Levels.title.priceTag('$', x + w - head * 0.62, y + head * 0.5, { size: Math.round(head * 0.3), rot: 0.12 });
      const cs = Math.round(colH * (docked ? 0.42 : 0.44));
      NB.write(`${b.xLabel} (x)`, (x + split) / 2, y + head + colH / 2, { font: 'ui', weight: 700, size: cs, color: PENCIL(), align: 'center' });
      NB.write(`${b.yLabel} (y)`, (split + x + w) / 2, y + head + colH / 2, { font: 'ui', weight: 700, size: cs, color: PENCIL(), align: 'center' });
      const valX = x + w - 34;
      for (const row of L.rows) {
        const r = b.rows[row.i];
        if (st.live) {
          const hit = UI._hit('tg-row' + st.bi + '_' + row.i, row.cx, row.cy, w, row.h);
          if (hit.hover && !st.marks[row.i]) { g.save(); g.globalAlpha *= 0.35; g.fillStyle = NB.PAL.hi; g.fillRect(x + 6, row.top + 4, w - 12, row.h - 8); g.restore(); }
          if (hit.clicked) this.tapRow(row.i);
        }
        const faded = st.marks[row.i];
        NB.write(xText(r), (x + split) / 2, row.cy, { size, weight: 700, align: 'center', color: INK() });
        NB.write(r.text, valX, row.cy, { size, weight: 700, align: 'right', color: INK() });
        if (st.keys && row.i < 9) NB.write(String(row.i + 1), x - 26, row.cy, { font: 'ui', weight: 700, size: 24, color: PENCIL(), align: 'center' });
        // ÷: its working (free); desktop / landscape only (the dock has its own)
        if (!docked && (st.spot || this.phase === 'p') && UI.iconButton('tg-chk' + st.bi + '_' + row.i, '÷', L.chkX, row.cy, { r: L.r, on: !!st.checked[row.i] })) this.check(row.i);
        // the pencil working beside a checked (or wrongly tapped) row; the red pen owns the bad row in the fix
        const showNote = !docked && st.checked[row.i] && !(st.fix && row.i === R.bad);
        if (showNote) this.noteAt(L, row.i, row.cy, faded, st.checked[row.i].at);
        if (faded) NB.tick(docked ? x + w + 50 : L.noteX + 14, row.cy, { s: docked ? 1.4 : 0.8, color: PENCIL(), t0: faded.at });
      }
      // the circled row (the student's or Rivera's)
      if (st.found >= 0) { const row = L.rows[st.found]; NB.circle(x + w / 2, row.cy, w * 0.52, rowH * 0.46, { t0: st.foundAt, dur: 0.4 }); }
      if (!st.fix) return;
      const t0 = st.fixAt + 0.35;
      if (R.legit) {
        L.rows.forEach((row, k) => NB.tick(x + (docked ? 70 : 40), row.cy, { s: docked ? 1.5 : 1, t0: t0 + k * 0.12 }));
        return;
      }
      // Rivera's red pen: strike the wrong value, write the honest one beside it
      const row = L.rows[R.bad], bad = b.rows[R.bad], hon = b.honest[R.bad];
      const vw = tw(bad.text, handF(size, 700));
      NB.strike(valX - vw - 10, row.cy + size * 0.18, valX + 10, row.cy - size * 0.18, { t0, w: docked ? 8 : 6 });
      const nx = docked ? x + w + 40 : L.noteX, ns = docked ? 76 : Math.round(UI.text(40));
      NB.note(hon.text, nx, row.cy, -0.03, { t0: t0 + 0.3, size: ns, weight: 700 });
    }
    /* the working of row i as a pencil note in the table's note column (shrunk to fit before the panel) */
    noteAt(L, i, cy, faded, at) {
      const text = this.working(i), maxW = L.panelX - 24 - (L.noteX + (faded ? 44 : 0));
      let fs = Math.round(UI.text(26));
      const w0 = tw(text, uiF(fs));
      if (w0 > maxW) fs = Math.max(16, Math.floor(fs * maxW / w0));
      const hot = i === this.lastCheck && T - this.lastCheckAt < 1.2;
      if (hot) NB.highlight(L.noteX + (faded ? 44 : 0) - 4, cy - fs * 0.7, tw(text, uiF(fs)) + 8, fs * 1.4, { t0: this.lastCheckAt, dur: 0.25, alpha: 0.5 });
      NB.write(text, L.noteX + (faded ? 44 : 0), cy, { font: 'ui', size: fs, color: faded ? PENCIL() : INK(), t0: at, dur: 0.3 });
    }

    /* ---- the ink graph ---- */
    drawGraph(R, L, st) {
      const b = R.b, G = L.G, docked = L.mode === 'd', { x, y, w, h, head, ox, oy, X, Y, sx, sy, fs } = L;
      const t0 = this.boardAt;
      NB.indexCard(x, y, w, h, { lines: false });
      g.save(); g.fillStyle = RED(); g.fillRect(x, y + head - 1, w, 2.5); g.restore();
      NB.write(b.title, x + 30, y + head * 0.52, { size: Math.round(head * 0.5), weight: 700 });
      // the graph's own grid: a square per step, heavier on the labelled lines
      const every = [1, 2, 5, 10, 20].find(k => G.ySquares / k <= 8) || 50;
      g.save();
      g.lineWidth = 1.2; g.strokeStyle = NB.PAL.grid; g.beginPath();
      for (let i = 1; i <= G.xSquares; i++) { g.moveTo(X(i), oy); g.lineTo(X(i), oy - G.ySquares * sy); }
      for (let j = 1; j <= G.ySquares; j++) { if (j % every) { g.moveTo(ox, oy - j * sy); g.lineTo(ox + G.xSquares * sx, oy - j * sy); } }
      g.stroke();
      g.lineWidth = 2; g.strokeStyle = NB.PAL.gridMajor; g.beginPath();
      for (let j = every; j <= G.ySquares; j += every) { g.moveTo(ox, oy - j * sy); g.lineTo(ox + G.xSquares * sx, oy - j * sy); }
      g.stroke();
      // axes in ink with ticks
      g.lineWidth = docked ? 6 : 4; g.strokeStyle = INK(); g.lineCap = 'round'; g.beginPath();
      g.moveTo(ox, oy); g.lineTo(ox + G.xSquares * sx + 16, oy); g.moveTo(ox, oy); g.lineTo(ox, oy - G.ySquares * sy - 16); g.stroke();
      g.lineWidth = 3; g.beginPath();
      for (let i = 1; i <= G.xSquares; i++) { g.moveTo(X(i), oy - 7); g.lineTo(X(i), oy + 7); }
      for (let j = every; j <= G.ySquares; j += every) { g.moveTo(ox - 7, oy - j * sy); g.lineTo(ox + 7, oy - j * sy); }
      g.stroke(); g.restore();
      // after the answer: the honest line, faint, from (0, 0) out; the origin circled in highlighter
      if (st.fix) {
        const lt = st.fixAt + 0.2, xe = Math.min(G.xSquares, G.yMax / b.p);
        NB.pen([[ox, oy], [X(xe), Y(b.p * xe)]], { w: docked ? 6 : 4, color: PENCIL(), alpha: 0.55, prog: E.inOutCubic(prog(lt, 0.7)), step: 10 });
        if (T > lt + 0.7) NB.circle(ox, oy, docked ? 40 : 28, docked ? 36 : 24, { t0: lt + 0.7, dur: 0.3, color: NB.PAL.hi, w: docked ? 18 : 13 });
      }
      for (let i = 0; i <= G.xSquares; i++) NB.write(String(i), X(i), oy + fs * 1.05, { font: 'ui', size: fs, color: PENCIL(), align: 'center' });
      for (let j = every; j <= G.ySquares; j += every) NB.write(Ratio.text(Ratio.mul(j, Ratio.frac(G.yStep))), ox - 14, oy - j * sy, { font: 'ui', size: fs, color: PENCIL(), align: 'right' });
      const an = L.an;
      NB.write(`${b.xLabel} (x)`, ox + G.xSquares * sx / 2, L.nameY, { font: 'ui', weight: 700, size: an, color: PENCIL(), align: 'center' });
      NB.write(`${b.yLabel}${b.money ? ' ($)' : ''} (y)`, ox - 10, L.plotTop - an * 1.1, { font: 'ui', weight: 700, size: an });
      // the points: small ink dots that press in after the drop
      for (const row of L.rows) {
        const i = row.i, ds = E.outBack(prog(t0 + DROP * 0.8 + i * 0.07, 0.2), 2);
        if (st.live) {
          const hit = UI._hit('tg-pt' + st.bi + '_' + i, row.hx, row.hy, row.hw, row.hh);
          if (hit.hover && !st.marks[i]) NB.circle(row.px, row.py, L.dot * 2.4, L.dot * 2.4, { color: NB.PAL.hi, w: 10 });
          if (hit.clicked) this.tapRow(i);
        }
        if (ds > 0) { g.save(); g.fillStyle = st.marks[i] ? PENCIL() : INK(); g.beginPath(); g.arc(row.px, row.py, L.dot * ds, 0, TAU); g.fill(); g.restore(); }
        if (st.keys && i < 9) NB.write(String(i + 1), row.px - L.dot - 14, row.py - L.dot - 16, { font: 'ui', weight: 700, size: 22, color: PENCIL(), align: 'center' });
        if (st.marks[i]) NB.tick(row.px + L.dot + 22, row.py - 4, { s: docked ? 1.2 : 0.7, color: PENCIL(), t0: st.marks[i].at });
        else if (st.checked[i] && !docked && !st.fix) NB.write('✓', row.px + L.dot + 12, row.py - L.dot - 8, { font: 'ui', size: 20, color: PENCIL() });
        // ÷ under the point's x: its working (free)
        if (!docked && (st.spot || this.phase === 'p') && UI.iconButton('tg-chk' + st.bi + '_' + i, '÷', row.px, L.chipY, { r: L.r, on: !!st.checked[i] })) this.check(i);
      }
      if (st.found >= 0) { const row = L.rows[st.found]; NB.circle(row.px, row.py, L.dot * 2.6, L.dot * 2.6, { t0: st.foundAt, dur: 0.4 }); }
      if (!st.fix || R.legit) return;
      // Rivera's red pen: the honest point (hollow), an arrow from the wrong one, its honest value
      const ft = st.fixAt + 0.5, row = L.rows[R.bad], hon = b.honest[R.bad], hx = X(hon.x), hy = Y(hon.y);
      const p = prog(ft, 0.3);
      if (p > 0) {
        g.save(); g.globalAlpha *= p; g.lineWidth = docked ? 6 : 4; g.strokeStyle = RED();
        g.beginPath(); g.arc(hx, hy, L.dot * 1.05, 0, TAU); g.stroke(); g.restore();
        const dy = hy - row.py, len = Math.abs(dy), dir = Math.sign(dy) || 1;
        if (len > L.dot * 3) {
          const a0 = row.py + dir * L.dot * 1.6, a1 = hy - dir * L.dot * 1.6;
          NB.pen([[row.px, a0], [row.px, a1]], { w: docked ? 6 : 4, color: RED(), prog: prog(ft + 0.1, 0.25), step: 6 });
          NB.pen([[row.px - 9, a1 - dir * 12], [row.px, a1], [row.px + 9, a1 - dir * 12]], { w: docked ? 6 : 4, color: RED(), prog: prog(ft + 0.35, 0.15), step: 4 });
        }
      }
      const lbl = `(${xText(hon)}, ${hon.text})`, ns = docked ? 56 : Math.round(UI.text(32));
      const lx = Math.min(hx + L.dot * 2 + 8, x + w - 20 - tw(lbl, handF(ns, 700)));
      NB.note(lbl, lx, hy + (hy > row.py ? ns * 0.9 : -ns * 0.2), -0.03, { t0: ft + 0.4, size: ns, weight: 700 });
    }

    /* ---------------- the right-hand panel (desktop / landscape) ---------------- */
    drawSpot(L) {
      const R = this.R, X = L.panelX, W = 1880 - X, fs = UI.text(32), hs = UI.text(46), u = unitOf(R);
      const body = R.graph
        ? ['Is this graph proportional?', `Each point is (x, y). Every point should give the same y ÷ x.`, `Tap the point that breaks the pattern, or All proportional.`]
        : ['Is this board proportional?', `Divide y by x in every row: they should all give the same number.`, `Tap the row that breaks the pattern, or All proportional.`];
      const wrapped = body.flatMap((l, i) => UI._wrap(l, W - 70, uiF(fs, i ? 400 : 700)).map(t => [t, i]));
      const head = hs * 1.8, h = head + wrapped.length * fs * 1.35 + 24;
      const hs0 = UI.hudSlot(0), top = Math.max(215, hs0.y + hs0.r + 30);
      NB.indexCard(X, top, W, h, { header: head, pitch: fs * 1.35 });
      NB.write(`Board ${this.bi + 1} of ${this.n}`, X + 34, top + head * 0.5, { size: hs, weight: 700, t0: this.boardAt });
      wrapped.forEach(([t, i], k) => NB.write(t, X + 34, top + head + (k + 0.5) * fs * 1.35 + 6, { font: 'ui', weight: i ? 400 : 700, size: fs, t0: this.boardAt + 0.2 + k * 0.08, dur: 0.25 }));
      const bh = UI.tap(92), by = top + h + 26 + bh / 2;
      if (Game.time - this.boardAt > 0.5 && UI.button('tg-legit', (UI.keys ? 'A  ' : '') + 'All proportional ✓', X + W / 2, by, { w: Math.min(W - 40, 520), h: 92, secondary: true })) this.legit();
      let y = by + bh / 2 + 34;
      const ns = UI.text(30);
      if (this.say && T - this.say.at < 6) {
        const L2 = UI._wrap(this.say.text, W - 40, handF(ns));
        L2.forEach((t, k) => NB.note(t, X + 20, y + k * ns * 1.25, -0.01, { t0: this.say.at + k * 0.3, size: ns, dur: 0.35 }));
        y += L2.length * ns * 1.25 + 14;
      }
      // a graph's checked points: their working here (a table writes it beside the row)
      if (R.graph) {
        const ks = Object.keys(this.checked).map(Number).sort((a, b) => a - b);
        if (!ks.length) NB.write(`Tap ÷ under a ${u} to see its y ÷ x.`, X + 20, y + 10, { font: 'ui', size: UI.text(26), color: PENCIL() });
        const ws = UI.text(28);
        ks.forEach((i, k) => { if (y + 10 + k * ws * 1.3 < 1070) NB.write(`${rowLabel(R, i)}:  ${this.working(i)}`, X + 20, y + 10 + k * ws * 1.3, { font: 'ui', size: ws, color: this.marks[i] ? PENCIL() : INK(), t0: this.checked[i].at, dur: 0.3 }); });
      } else if (!Object.keys(this.checked).length) NB.write(`Tap ÷ beside a ${u} to see its y ÷ x.`, X + 20, y + 10, { font: 'ui', size: UI.text(26), color: PENCIL() });
    }
    /* "What's p?": the question on the red line, the options on the blue lines, crossed-out wrong picks + why */
    pCard(L) {
      const X = L.panelX, W = 1880 - X, q = this.inst, qs = UI.text(40), os = UI.text(40);
      const intro = this.revealed ? (this.R.legit ? 'It was all proportional.' : 'Ms. Rivera circled it.') : this.R.legit ? 'Right: all proportional!' : `Got it: that ${unitOf(this.R)} is off.`;
      const hs0 = UI.hudSlot(0), top0 = hs0.y + hs0.r + 16;
      const last = this.picks[this.picks.length - 1], why = last != null && last !== q.answerIndex ? q.why[last] : '';
      const rows = q.options.map(() => Math.max(UI.tap(92) + 4, os * 1.9));
      let C;
      for (const [withHint, k] of [[true, 1], [false, 1], [false, 0.85], [false, 0.72]]) {
        const is = UI.text(30) * k, iL = UI._wrap(intro, W - 80, handF(is, 700)), qL = UI._wrap(q.text, W - 80, uiF(qs * k, 700));
        const head = 40 + iL.length * is * 1.3 + qL.length * qs * k * 1.25;
        const ws = UI.text(30) * k, whyL = why ? UI._wrap(why, W - 80, handF(ws)) : [];
        const hs = UI.text(26) * k, hintL = withHint ? UI._wrap('p = y ÷ x, the value for ONE. Use an honest ' + unitOf(this.R) + '.', W - 80, uiF(hs)) : [];
        const h = head + rows.reduce((a, b) => a + b, 0) + 20 + hintL.length * hs * 1.3 + 10 + whyL.length * ws * 1.3 + (whyL.length ? 16 : 0);
        C = { X, W, q, qs: qs * k, os, qL, head, rows, whyL, ws, hintL, hs, h, top: Math.max(top0, Math.min(215, 1070 - h)), iL, is };
        if (top0 + h <= 1070) break;
      }
      return C;
    }
    drawP(L) {
      const C = this.pCard(L), { X, W, q, qs, os, qL, head, rows, whyL, ws, hintL, hs, h, top, iL, is } = C, t0 = this.pAt;
      if (T < t0) return;
      NB.indexCard(X, top, W, h, { header: head, pitch: 1e4 });
      iL.forEach((l, i) => NB.note(l, X + 34, top + 20 + (i + 0.5) * is * 1.3, 0, { t0, size: is, weight: 700, dur: 0.3 }));
      const qy = top + 20 + iL.length * is * 1.3;
      qL.forEach((l, i) => NB.write(l, X + 34, qy + (i + 0.5) * qs * 1.25, { font: 'ui', weight: 700, size: qs, t0: t0 + 0.2 + i * 0.15, dur: 0.3 }));
      let y = top + head;
      q.options.forEach((o, i) => {
        const rh = rows[i], cy = y + rh / 2, kx = X + 60, crossed = this.picks.includes(i);
        g.save(); g.fillStyle = NB.PAL.cardLine; g.fillRect(X, y + rh - 1, W, 1.5); g.restore();
        const hit = !crossed ? UI._hit('tg-opt' + this.bi + '_' + i, X + W / 2, cy, W - 20, rh - 2) : { hover: false, clicked: false };
        if (hit.hover) { g.save(); g.globalAlpha *= 0.35; g.fillStyle = NB.PAL.hi; g.fillRect(X + 12, y + 6, W - 24, rh - 12); g.restore(); }
        g.save(); g.lineWidth = 3; g.strokeStyle = INK(); g.strokeRect(kx - 20, cy - 20, 40, 40);
        g.font = uiF(24, 700); g.fillStyle = INK(); g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(String(i + 1), kx, cy + 1); g.restore();
        const mw = NB.write('p = ' + o, kx + 50, cy, { size: os, weight: 700, color: crossed ? PENCIL() : INK(), t0: t0 + 0.2 + i * 0.1, dur: 0.25 });
        if (crossed) NB.strike(kx + 40, cy - 16, kx + 60 + mw, cy + 16, { t0: i === last(this.picks) ? this.pickedAt : -1e9, w: 5 });
        if (hit.clicked) this.choose(i);
        y += rh;
      });
      hintL.forEach((l, k) => NB.write(l, X + 34, y + 20 + (k + 0.5) * hs * 1.3, { font: 'ui', size: hs, color: PENCIL(), t0: t0 + 0.5 + k * 0.2, dur: 0.3 }));
      y += 20 + hintL.length * hs * 1.3 + 10;
      whyL.forEach((l, k) => NB.note(l, X + 34, y + (k + 0.5) * ws * 1.3, 0, { t0: this.pickedAt + 0.3 + k * 0.3, size: ws, dur: 0.35 }));
    }
    /* Rivera's card: y ÷ x for every row (the odd one circled), the item's explain, this board's stars */
    fixLines() {
      const R = this.R, res = this.results[this.bi] || { stars: 0, rowStar: 0, pStar: 0 }, out = [];
      out.push(['head', R.legit ? (res.rowStar ? 'All proportional!' : 'It was all proportional.') : 'Ms. Rivera checks every ' + unitOf(R)]);
      R.b.rows.forEach((r, i) => out.push(['work', this.working(i), i]));
      out.push(['rule', this.inst.explain || '']);
      out.push(['stars', `+${res.stars} star${res.stars === 1 ? '' : 's'} (${unitOf(R) === 'point' && !R.legit ? 'point' : R.legit ? 'all proportional' : 'row'} ${res.rowStar}, p ${res.pStar})`]);
      return out;
    }
    fixCard(L) {
      const key = this.bi + '|' + Math.round(UI.text(28)) + '|' + L.panelX;
      if (this._fc && this._fc.key === key) return this._fc;
      const F = this.fixLines(), bh = UI.tap(92), hs0 = UI.hudSlot(0), top0 = hs0.y + hs0.r + 24, X = L.panelX, W = 1880 - X;
      let C;
      for (const k of [1, 0.9, 0.8, 0.72, 0.64]) {
        const S = { head: [UI.text(44) * k, handF(UI.text(44) * k, 700)], work: [UI.text(30) * k, uiF(UI.text(30) * k)], rule: [UI.text(32) * k, handF(UI.text(32) * k)], stars: [UI.text(34) * k, handF(UI.text(34) * k, 700)] };
        const lh = kk => S[kk][0] * 1.32;
        const rows = F.flatMap(([kk, t, i]) => UI._wrap(t, W - (kk === 'work' ? 110 : 70), S[kk][1]).map((l, j) => [kk, l, j > 0, i]));
        const h = rows.reduce((a, [kk]) => a + lh(kk), 0) + 36;
        C = { key, X, W, rows, h, S, lh, bh, top: Math.max(96, Math.min(top0, 1080 - 14 - bh - 22 - h)) };
        if (top0 + h + 22 + bh + 14 <= 1080) break;
      }
      return (this._fc = C);
    }
    drawFix(L) {
      const C = this.fixCard(L), { X, W, rows, h, S, lh, bh, top } = C, t0 = this.fixAt + 0.2, R = this.R;
      NB.indexCard(X, top, W, h, { header: rows.filter(([k]) => k === 'head').length * lh('head') + 20, pitch: 1e4 });
      let y = top + 18, tt = t0;
      let wasHead = true;
      rows.forEach(([k, l, cont, i]) => {
        if (wasHead && k !== 'head') { y += 6; wasHead = false; }
        const s = S[k][0], cy = y + lh(k) / 2 + (k === 'head' ? 2 : 8);
        const o = { size: s, t0: tt, dur: Math.max(0.2, Math.min(0.5, l.length * 0.02)) };
        if (k === 'head') NB.write(l, X + 34, cy, { ...o, weight: 700, color: RED() });
        else if (k === 'rule') NB.note(l, X + 34, cy, 0, o);
        else if (k === 'work') {
          const tx = X + 34 + s * 1.6;
          if (!cont) NB.write(R.graph ? '•' : String(i + 1), X + 34 + s * 0.5, cy, { font: 'ui', weight: 700, size: s * 0.8, color: PENCIL(), align: 'center', t0: tt });
          const mw = NB.write(l, tx, cy, { ...o, font: 'ui', color: INK() });
          if (i === R.bad && !cont) NB.circle(tx + mw / 2, cy, mw / 2 + 22, s * 0.78, { t0: tt + o.dur, dur: 0.4 });
          else if (!cont) NB.tick(tx + mw + 30, cy, { s: 0.6, color: PENCIL(), t0: tt + o.dur });
        }
        else NB.write(l, X + 34, cy, { ...o, weight: 700, color: RED() });
        tt += o.dur * 0.6; y += lh(k);
      });
      const label = this.bi < this.n - 1 ? 'Next board ▶' : 'See my stars ▶';
      if (Game.time - this.fixAt > NEXT_GUARD && UI.button('tg-next', label, X + W / 2, top + h + 22 + bh / 2, { w: Math.min(W - 40, 440), h: 92, primary: true })) this.next();
    }

    /* ---------------- portrait: the board is the picture; everything else in the dock ---------------- */
    drawDock(L) {
      const R = this.R, q = this.inst, id = this.attempt + '_' + this.bi, u = unitOf(R), n = R.n;
      Dock.head(`Board ${this.bi + 1} of ${this.n}`, `${this.stars} of ${this.of} stars`);
      const opts = R.b.rows.map((r, i) => rowLabel(R, i));
      const cols = R.graph ? `Points (${R.b.xLabel.toLowerCase()}, ${R.b.yLabel.toLowerCase()})` : `Rows: ${R.b.xLabel.toLowerCase()} → ${R.b.yLabel.toLowerCase()}`;
      const notes = R.b.rows.map((r, i) => (this.checked[i] ? (this.marks[i] ? 'fine: ' : '') + this.working(i) : ''));
      if (this.phase === 'drop' || this.phase === 'spot') {
        Dock.text('tg-how', [`Divide y by x for every ${u}: they should all match. Tap the ${u} that breaks the pattern, or All proportional.`], { cls: 'note' });
        if (this.phase === 'spot') {
          Dock.choices('tg-rows' + id, { prompt: cols, options: opts, state: R.b.rows.map((r, i) => (this.marks[i] ? 'faded' : '')), notes, onChoose: i => this.tapRow(i) });
          Dock.buttons('tg-legit' + id, [{ label: 'All proportional ✓', wide: true, onClick: () => this.legit() }]);
          if (this.say) Dock.text('tg-say' + id + '_' + this.misses, [this.say.text], { cls: 'wrong', reveal: true });
          this.dockChecks(id, n, u);
        }
        return;
      }
      if (this.phase === 'p') {
        const found = this.found >= 0 ? (this.revealed ? 'Ms. Rivera circled: ' : 'You circled: ') + opts[this.found] : (this.revealed ? 'It was all proportional.' : 'Right: all proportional!');
        Dock.text('tg-found' + id, [found], { cls: this.revealed ? 'note' : 'right' });
        const st = q.options.map((o, i) => (this.picks.includes(i) ? 'wrong' : ''));
        Dock.choices('tg-p' + id, { prompt: q.text, options: q.options.map(o => 'p = ' + o), state: st, notes: q.options.map((o, i) => (this.picks.includes(i) ? q.why[i] : '')), onChoose: i => this.choose(i) });
        Dock.text('tg-phint' + id, ['p = y ÷ x, the value for ONE. Use an honest ' + u + '.'], { cls: 'small' });
        this.dockChecks(id, n, u);
        return;
      }
      // fix
      const F = this.fixLines();
      Dock.choices('tg-p' + id, { prompt: q.text, options: q.options.map(o => 'p = ' + o), state: q.options.map((o, i) => (i === q.answerIndex ? 'right' : this.picks.includes(i) ? 'wrong' : 'faded')), locked: true });
      Dock.heading('tg-fix' + id, F[0][1]);
      Dock.list('tg-work' + id, R.b.rows.map((r, i) => ({ badge: String(i + 1), color: i === R.bad ? NB.PAL.redpen : NB.PAL.ink,
        lines: i === R.bad ? [`${opts[i]}: ${this.working(i)} ✗ the odd one`, `honest: ${rowLabel({ b: { rows: R.b.honest }, graph: R.graph }, i)}`] : [opts[i], this.working(i)] })));
      Dock.text('tg-rule' + id, [this.inst.explain || ''], { cls: 'note' });
      Dock.text('tg-stars' + id, F.filter(([k]) => k === 'stars').map(([, t]) => t), { cls: 'right', reveal: true });
      Dock.button({ id: 'next', glyph: this.bi < this.n - 1 ? 'Next board ▶' : 'See my stars ▶', label: 'next', aria: 'next board', go: true, onClick: () => this.next() });
    }
    /* portrait: a ÷ per row (free: it only shows that row's y ÷ x) */
    dockChecks(id, n, u) {
      Dock.text('tg-chkq' + id, [`Check a ${u} (free): tap its ÷`], { cls: 'small' });
      Dock.buttons('tg-chkb' + id, Array.from({ length: n }, (v, i) => ({ label: '÷ ' + (i + 1), onClick: () => this.check(i) })));
      if (this.lastCheck >= 0) Dock.text('tg-chkt' + id + '_' + this.lastCheck, [`${u[0].toUpperCase() + u.slice(1)} ${this.lastCheck + 1}: ` + this.working(this.lastCheck)], { cls: 'note', reveal: true });
    }

    drawIntro(docked) {
      const t0 = this.enteredAt, n = this.n, pass = this.passStars, of = this.of;
      const lines = ['Jay made price boards for Fresh Soles. Some are honest.', `${n} boards. All but one have a row (or point) that breaks the pattern.`,
        'Divide y by x. Tap the odd one out, then find p.', `${pass} of ${of} stars for the stamp.`];
      // a sample honest board (the model's numbers: $12 a pair)
      if (!this._demo) this._demo = { b: Ratio.board({ id: 'demo', kind: 'table', title: 'FRESH SOLES', x: 'PAIRS', y: 'COST', money: true, xs: [1, 2, 3], cheat: 'legit' }, { p: 12 }) };
      const R = { b: this._demo.b, legit: true, bad: -1, graph: false, n: 3 };
      const st = { docked, spot: false, live: false, marks: {}, checked: {}, found: -1, fix: false, bi: -1, keys: false, lastCheck: -1 };
      if (docked) {
        const L = tableLay(R.b, 'd');
        g.save(); g.translate(-130, 60); this.drawTable(R, L, st); g.restore();
        Dock.heading('tg-intro', 'Price Tags', { cls: 'big' });
        Dock.text('tg-intro', lines);
        Dock.button({ id: 'go', glyph: 'Start ▶', label: 'start', aria: 'Start checking price boards', go: true, color: 'red', onClick: () => this.begin() });
        return;
      }
      const L = tableLay(R.b, 'm');
      g.save(); g.translate(20, 40); this.drawTable(R, L, st); g.restore();
      NB.title('Price Tags', 1320, 250, { size: 110, t0: t0 + 0.2, hi: 'Tags' });
      const fs = UI.text(38);
      let y = 400;
      lines.forEach((l, i) => UI._wrap(l, 1060, uiF(fs, i === 3 ? 700 : 400)).forEach(t => { NB.write(t, 1320, y, { font: 'ui', size: fs, weight: i === 3 ? 700 : 400, align: 'center', t0: t0 + 0.5 + i * 0.3, dur: 0.45 }); y += fs * 1.45; }));
      if (this.t > 0.8 && UI.button('tg-start', 'Start ▶', 1300, Math.min(1000, y + 90), { w: 420, h: 110, primary: true })) this.begin();
      if (this.t > 1.2) NB.write(UI.hint('(or press Enter)', ''), 1300, Math.min(1000, y + 90) + 80, { font: 'ui', size: UI.text(26), color: PENCIL(), align: 'center' });
    }
    drawEnd(docked) {
      const s = this.stars, of = this.of, btns = this.endButtons();
      const rows = this.instances.map((inst, i) => { const r = this.results[i] || { stars: 0 }; return { r, text: `${i + 1}. ${inst.item.title}: ${SHORT[inst.item.cheat] || inst.item.cheat}` }; });
      const word = this.passed ? 'PROVEN' : 'TRY AGAIN';
      if (docked) {
        NB.title(`${s} / ${of}`, 960, 380, { size: 200, t0: this.endAt + 0.1, hi: String(s) });
        UI.stamp(word, 960, 720, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.6 });
        Dock.heading('tg-end', `Your stars: ${s} / ${of}`, { cls: 'big' });
        Dock.text('tg-end', [this.passed ? 'Proven! Collect your stamp.' : `Not yet: ${this.passStars} stars for the stamp. New boards?`]);
        Dock.list('tg-end', rows.map((x, i) => ({ badge: String(i + 1), color: x.r.stars === 2 ? NB.PAL.ink : NB.PAL.redpen, lines: [x.text.replace(/^\d+\. /, ''), `${x.r.stars} of 2 stars`] })));
        Dock.text('tg-code', [`Quiz code ${this.seed}`], { cls: 'small' });
        Dock.buttons('tg-end', btns.map((b, i) => ({ label: b.label, wide: i === 0, onClick: b.go })), { bottom: true });
        return;
      }
      NB.title(`${s} / ${of}`, 640, 250, { size: 140, t0: this.endAt + 0.1, hi: String(s) });
      UI.stamp(word, 1330, 250, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.1 });
      const fs = UI.text(34), rh = Math.max(66, fs * 1.4), bh = UI.tap(100), ch = rows.length * rh + 50;
      const cardTop = Math.min(350, 1080 - 40 - bh - 70 - ch - 20), top = cardTop + 25 + rh / 2;
      NB.indexCard(260, cardTop, 1400, ch, { header: 25, pitch: rh });
      rows.forEach((x, i) => {
        const y = top + i * rh;
        NB.write(x.text, 320, y, { font: 'ui', size: fs, t0: this.endAt + 0.3 + i * 0.1, dur: 0.3 });
        NB.write(`${x.r.stars} of 2`, 1580, y, { size: fs * 1.15, weight: 700, color: x.r.stars === 2 ? INK() : RED(), align: 'right', t0: this.endAt + 0.4 + i * 0.1 });
        if (x.r.stars === 2) NB.tick(1620, y, { s: 0.9, t0: this.endAt + 0.5 + i * 0.1 });
      });
      const py = cardTop + ch + 40, by = Math.min(1080 - 40 - bh / 2, py + 40 + bh / 2);
      NB.write(this.passed ? 'Proven! Collect your stamp.' : `Not yet: ${this.passStars} stars for the stamp.`, 960, py, { font: 'ui', size: UI.text(34), weight: 700, align: 'center', t0: this.endAt + 0.8, dur: 0.4 });
      btns.forEach((b, i) => { if (this.t > 0.2 && UI.button('tg-end' + i, b.label, 640 + i * 520, by, { w: 480, h: 100, primary: i === 0, secondary: i > 0 })) b.go(); });
      NB.write(`quiz code ${this.seed}`, 1860, by + bh / 2 + 24, { font: 'mono', size: UI.text(22), color: PENCIL(), align: 'right', t0: this.endAt + 0.6 });
    }
  }
  const last = a => a[a.length - 1];
  Tags.STEP = STEP; Tags.pool = pool; Tags.round = round; Tags.tableLay = tableLay; Tags.graphLay = graphLay;
  /* the live scene's solution (tests) */
  Object.defineProperty(Tags, 'solution', { get() { const s = Game.current(); return s instanceof Tags ? s.solution : null; } });
  /* the tap rects of the live scene's board on the canvas (tests tap them): {i, x, y, w, h} centred */
  Tags.rows = () => {
    const s = Game.current();
    if (!(s instanceof Tags) || !s.R) return [];
    const L = s.lay(UI.docked());
    return L.kind === 'graph' ? L.rows.map(r => ({ i: r.i, x: r.hx, y: r.hy, w: r.hw, h: r.hh })) : L.rows.map(r => ({ i: r.i, x: r.cx, y: r.cy, w: r.w, h: r.h }));
  };
  Levels.tags = Tags;
})();
